import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function BottomSheet({
  open,
  onClose,
  title,
  subtitle,
  children
}: BottomSheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);
  return <AnimatePresence data-id="element-34" data-mp="73i8gempeq0v">
      {open && <div className="fixed inset-0 z-50 flex justify-center" data-id="element-35" data-mp="76eaae1o9yul">
          <motion.button type="button" aria-label="Fechar" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.28,
        ease: EASE
      }} className="absolute inset-0 cursor-default bg-ink/40 backdrop-blur-[2px]" data-id="element-36" data-mp="246ufddq1ymz" />
          <motion.div role="dialog" aria-modal="true" aria-label={title} initial={{
        y: '100%',
        opacity: 0.5
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: '100%',
        opacity: 0.5
      }} transition={{
        y: {
          duration: 0.3,
          ease: EASE
        },
        opacity: {
          duration: 0.22,
          ease: EASE
        }
      }} drag="y" dragConstraints={{
        top: 0,
        bottom: 0
      }} dragElastic={{
        top: 0,
        bottom: 0.4
      }} onDragEnd={(_, info) => {
        if (info.offset.y > 120 || info.velocity.y > 600) onClose();
      }} className="relative mt-auto flex max-h-[92vh] w-full max-w-[460px] flex-col rounded-t-[28px] bg-cream shadow-sheet" data-id="element-37" data-mp="1ffaiit3btkmw">
            <div className="shrink-0 px-4 pb-3 pt-3" data-id="element-38" data-mp="3o2ymenp3pn9x">
              <div className="mx-auto h-1 w-10 rounded-full bg-shell" aria-hidden="true" data-id="element-39" data-mp="3o26zs2dhezvs" />
              <div className="mt-4 flex items-start justify-between gap-4" data-id="element-40" data-mp="5necybluy4uw">
                <div data-id="element-41" data-mp="pcsbm8s9xhb">
                  <h2 className="font-display text-[26px] leading-tight text-ink" data-id="element-42" data-mp="q626w31h33o">{title}</h2>
                  {subtitle && <p className="mt-1 text-[13px] text-muted" data-id="element-43" data-mp="395y23598oulh">{subtitle}</p>}
                </div>
                <button type="button" onClick={onClose} aria-label="Fechar" className="-mr-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-shell text-muted transition-colors duration-150 ease-out hover:bg-sand hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mocha" data-id="element-44" data-mp="355fb1df67skc">
                  <XIcon className="h-4 w-4" strokeWidth={1.6} data-id="element-45" data-mp="2bbf16fq9j7sk" />
                </button>
              </div>
            </div>
            <motion.div initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.24,
          ease: EASE,
          delay: 0.08
        }} className="no-scrollbar overflow-y-auto overscroll-contain px-4 pb-8" data-id="element-46" data-mp="2gc2nwp7gl2gz">
              {children}
            </motion.div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}